import { FormEvent, useState } from "react";
import useFieldTypes from "../../../../api/api-hooks/cloud/useFieldTypes";
import AppFieldCard from "../../../../elements/AppFieldCard";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import Constants from "../../../../utils/Constants";
import FieldUtils from "../../../../utils/FieldUtils";
import StrUtils from "../../../../utils/StrUtils";
import AppInput from "../../../AppInput";

interface Props {
  fieldValues: any;
  setFieldValues: any;
  disableSubitem: boolean;
  availableFieldTypes: any;
  searchEnabled?: boolean;
  handleSubmit?: () => void;
}

const FieldTypeManager = ({
  fieldValues,
  setFieldValues,
  disableSubitem,
  availableFieldTypes,
  searchEnabled,
  handleSubmit,
}: Props) => {
  const [search, setSearch] = useState("");
  const { fieldTypesLoading, fieldTypes } = useFieldTypes();

  const getFieldTypes = () => {
    if (!fieldTypes || !Array.isArray(fieldTypes)) return [];

    return fieldTypes?.flatMap((type: any) => {
      if (availableFieldTypes && !availableFieldTypes.includes(type.APIName)) {
        return [];
      }

      if (
        disableSubitem &&
        (FieldUtils.fieldTypeIsSubitem(type.APIName) ||
          type.APIName === "Calculated")
      ) {
        return [];
      }

      if (searchEnabled && search) {
        const s = search.toLowerCase();

        if (
          !type.Name.toLowerCase().includes(s) &&
          !type.APIName.toLowerCase().includes(s)
        ) {
          return [];
        }
      }

      return type;
    });
  };

  const finalFieldTypes = getFieldTypes();

  const handleFieldTypeChange = (value: any) => {
    const newFieldValues = { ...fieldValues };
    newFieldValues.fieldType = value;

    // Reset values
    newFieldValues.source = "";
    newFieldValues.default = false;
    newFieldValues.defaultValue = "";
    newFieldValues.summary = false;
    newFieldValues.summaryFields = {};

    // If a select type was chosen, create source array
    if (
      value === Constants.FIELDTYPES.SINGLE_SELECT ||
      value === Constants.FIELDTYPES.MULTI_SELECT
    ) {
      newFieldValues.source = [
        { Name: "", Value: "", Color: "#e8edea", id: StrUtils.uuidv4() },
      ];
    }

    setFieldValues(newFieldValues);
  };

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (finalFieldTypes.length !== 1) return;

    handleFieldTypeChange(finalFieldTypes[0].APIName);
    handleSubmit && handleSubmit();
  };

  const LoadingRow = () => {
    return (
      <div className="d-flex my-3">
        <SkeletonLoader css="mx-2" height={55} />
        <SkeletonLoader css="mx-2" height={55} />
      </div>
    );
  };

  return (
    <>
      {fieldTypesLoading ? (
        <>
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
          <LoadingRow />
        </>
      ) : !fieldTypes?.length ? (
        <>Failed to load field types. Something went wrong.</>
      ) : (
        <>
          {searchEnabled && (
            <form className="tw:px-2 tw:pb-4" onSubmit={onHandleSubmit}>
              <AppInput
                placeholder="Search Fields"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
          )}
          <div className="tw:flex tw:flex-wrap">
            {finalFieldTypes.map((type: any) => {
              return (
                <AppFieldCard
                  key={type.APIName}
                  onClick={() => handleFieldTypeChange(type.APIName)}
                  isSelected={type.APIName === fieldValues.fieldType}
                  icon={type.Icon}
                  name={type.Name}
                  description={type.Description}
                  innerCSS="mx-2"
                />
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default FieldTypeManager;
